/**
 * Every knob in the relay's PenelopeGameConfig (relay/src/db/schema.ts; defaults
 * and clamps in relay/src/services/penelope/config.ts), grouped by what it
 * governs, with a plain-language hint. `enabled`, `mode`, `lean` and `variants`
 * have their own controls on the page and are not listed here. The relay clamps
 * whatever is sent, so the bounds below are only there to stop a typo.
 *
 * Two tiers (2026-10-08): a knob tagged `tier: 'basic'` is on the surface every
 * trader gets (the ticket, the /live strip); everything else is ADVANCED and sits
 * behind a disclosure a manager can switch off per member. Basic deliberately
 * holds no knob that can cancel a resting order — the first shadow night showed
 * those are the ones that cost the fills.
 */
export type Knob =
  | { key: string; label: string; hint: string; kind: 'number'; unit?: string; step?: number; min?: number; max?: number; tier?: 'basic' }
  | { key: string; label: string; hint: string; kind: 'bool'; tier?: 'basic' }
  | { key: string; label: string; hint: string; kind: 'select'; options: Array<{ value: string; label: string }>; tier?: 'basic' };

const n = (key: string, label: string, hint: string, o: { unit?: string; step?: number; min?: number; max?: number } = {}, tier?: 'basic'): Knob => ({ key, label, hint, kind: 'number', ...o, ...(tier ? { tier } : {}) });

export const KNOB_GROUPS: Array<{ title: string; knobs: Knob[] }> = [
  {
    title: 'Size and caps',
    knobs: [
      n('clipContracts', 'clip', 'Contracts per quote.', { min: 1, step: 1 }, 'basic'),
      n('maxNetContracts', 'max net', 'How far long one team she may run when there is no lean, or on the side that is not the lean.', { min: 1, step: 1 }, 'basic'),
      n('leanMaxNetContracts', 'lean max net', 'How far long the LEAN team she may run.', { min: 1, step: 1 }),
      n('maxGrossContracts', 'max gross', 'Total contracts held across both teams. Pairs stay open to settlement, so this only grows during a game.', { min: 1, step: 10 }),
      n('leanSkewCents', 'lean skew', 'Edge demanded is this much lower on the lean side and this much higher on the other.', { unit: '¢', step: 0.5 }),
    ],
  },
  {
    title: 'Value: quoting against Pinnacle',
    knobs: [
      n('minEdgeCents', 'edge to add', 'Pinnacle fair minus price minus net fee must be at least this before she adds to a side.', { unit: '¢', step: 0.5 }, 'basic'),
      n('cancelEdgeCents', 'edge to unwind', 'Selling it back on the other side needs only this much edge. A resting bid whose edge falls below it is pulled at once.', { unit: '¢', step: 0.5 }, 'basic'),
      n('improveCents', 'improve', '0 joins the best bid; 1 bids one cent better than it.', { unit: '¢', step: 1, min: 0 }),
      n('maxBehindCents', 'max behind', 'Do not rest further than this behind the best bid; she stands aside until the edge is there at a closer price.', { unit: '¢', step: 1, min: 0 }),
      n('maxFairBookGapCents', 'max fair-to-book gap', 'If Pinnacle and the Kalshi book disagree by more than this she pauses: one of them is wrong.', { unit: '¢', step: 1, min: 0 }),
    ],
  },
  {
    title: 'Fair freshness',
    knobs: [
      n('pinMaxVendorAgeMs', 'max Pinnacle age', 'A Pinnacle quote older than this, on the vendor clock, counts as the line being off.', { unit: 'ms', step: 1000, min: 0 }),
      n('gameQuietMaxMs', 'max game quiet', 'No sportsbook update for this game for this long: the fair is treated as stale.', { unit: 'ms', step: 1000, min: 0 }),
      n('laneQuietMaxMs', 'max feed quiet', 'No sportsbook update on the whole odds feed for this long: the feed is treated as dead and she pauses.', { unit: 'ms', step: 1000, min: 0 }),
      n('futureToleranceMs', 'future tolerance', 'A vendor stamp this far ahead of our clock is still accepted.', { unit: 'ms', step: 500, min: 0 }),
      n('returnConfirmMs', 'return confirm', 'After Pinnacle comes back, wait this long before quoting against it again.', { unit: 'ms', step: 500, min: 0 }),
    ],
  },
  {
    title: 'Resting orders',
    knobs: [
      { key: 'restPolicy', label: 'a resting bid comes off', hint: 'Stay: only for an integrity gate, its edge against the last Pinnacle line falling through the unwind edge, the pair lock, a cap, or a stop — never because Pinnacle pulled, the touch moved a cent, or the bid is a little behind. Chase: the old behaviour (pull policy, behind-touch cancels, touch-following requotes).', kind: 'select', options: [
        { value: 'stay', label: 'stay' }, { value: 'chase', label: 'chase (old)' },
      ] },
      n('restEdgeMaxAgeMs', 'judge against the last line for', 'Under stay, after Pinnacle drops, a resting bid is still judged against the line as of the drop for this long (vendor clock). 0 = only a live line may take a rest off; while the line is off only the lock and the caps do.', { unit: 'ms', step: 10_000, min: 0 }),
    ],
  },
  {
    title: 'When Pinnacle pulls its line',
    knobs: [
      { key: 'pullPolicy', label: 'on pull', hint: 'Chase only. What happens to resting bids at the instant the line goes away. Under stay they stay.', kind: 'select', options: [
        { value: 'cancelAll', label: 'cancel all' }, { value: 'cancelAdding', label: 'cancel adding side' }, { value: 'hold', label: 'hold' }, { value: 'widen', label: 'widen' },
      ] },
      n('pullSettleMs', 'pull settle', 'How long after the drop before she starts bracketing both sides (under stay: adds nothing for this long; the rests stay).', { unit: 'ms', step: 500, min: 0 }),
      n('pullWidenCents', 'pull widen', 'Chase only. With "widen": how far to step each resting bid back.', { unit: '¢', step: 1, min: 0 }),
    ],
  },
  {
    title: 'Pair: both sides while the line is off',
    knobs: [
      { key: 'pairEnabled', label: 'pair while off', hint: 'Bracket both sides while Pinnacle is away. Off = rest nothing until it returns.', kind: 'bool' },
      n('pairLockCents', 'pair lock', 'Bracket only where a filled pair locks at least this much.', { unit: '¢', step: 1, min: 0 }),
      n('pairOffsetCents', 'pair offset', 'Rest this far behind the best bid on each side while bracketing.', { unit: '¢', step: 1, min: 0 }),
      n('pairMaxUnpaired', 'max unpaired', 'Stop adding to the filled side once this many contracts are waiting for their other half.', { min: 1, step: 1 }),
      n('pairHangTimeoutSec', 'hang timeout', 'A half-filled pair older than this is "hung" and the hang defence applies.', { unit: 's', step: 5, min: 0 }),
      { key: 'pairHangDefense', label: 'hang defence', hint: 'Ride keeps waiting at the lock price; step to scratch gives the lock up to get out flat.', kind: 'select', options: [
        { value: 'stepToScratch', label: 'step to scratch' }, { value: 'ride', label: 'ride' },
      ] },
      { key: 'pairOnLaneDead', label: 'pair on a dead feed', hint: 'Keep bracketing even when the whole odds feed has gone quiet. Off is the safe setting.', kind: 'bool' },
    ],
  },
  {
    title: 'Book gates',
    knobs: [
      { key: 'liquidityGatePolicy', label: 'thin book', hint: 'What a thinning Kalshi book (one side gone, quiet, wide, depth dropout) does to her resting bids. Hold keeps them and adds nothing; pause cancels them (the old behaviour); quote carries on from the Pinnacle anchor (a depth dropout still only holds).', kind: 'select', options: [
        { value: 'hold', label: 'hold rests' }, { value: 'pause', label: 'pause (cancel)' }, { value: 'quote', label: 'keep quoting' },
      ] },
      n('bookStaleMs', 'book stale', 'Dead-subscription bound. Hydra sends a book only when it changes, so a quiet book is not a stale one — keep this generous (default 600 s).', { unit: 'ms', step: 1000, min: 250 }),
      n('minSpreadCents', 'min spread', 'A Kalshi spread tighter than this trips the thin-book gate.', { unit: '¢', step: 1, min: 0 }),
      n('maxSpreadCents', 'max spread', 'A Kalshi spread wider than this trips the thin-book gate.', { unit: '¢', step: 1, min: 1 }),
      n('depthDropPct', 'depth drop', 'Resting size near the touch falling to this fraction of its recent maximum (0.4 = 40%) trips the depth guard: hold under hold/quote, cancel under pause. 0 turns it off.', { step: 0.05, min: 0, max: 1 }),
      n('depthMinBaseline', 'depth baseline', 'The depth guard only arms once near-touch size has reached this many contracts.', { step: 50, min: 0 }),
      n('depthWindowMs', 'depth window', 'The "recent maximum" the depth guard compares against is taken over this window.', { unit: 'ms', step: 1000, min: 0 }),
      n('scoreHoldMs', 'score hold', 'Stand down this long after a score change. 0 = off.', { unit: 'ms', step: 1000, min: 0 }),
    ],
  },
  {
    title: 'Pacing',
    knobs: [
      n('requoteCents', 'requote step', 'Move a resting bid only when the wanted price differs by at least this.', { unit: '¢', step: 1, min: 1 }),
      n('minRestMs', 'min rest', 'A bid rests at least this long before a routine requote. Urgent pulls ignore it.', { unit: 'ms', step: 250, min: 0 }),
      n('resumeStableMs', 'resume stable', 'After a pause, every gate must stay clear this long before she quotes again.', { unit: 'ms', step: 250, min: 0 }),
      n('maxPlacesPerMin', 'max places / min', 'Hard ceiling on orders placed per minute for this game.', { step: 5, min: 1 }),
    ],
  },
  {
    title: 'Kill',
    knobs: [
      n('gameKillUsd', 'game kill', 'She stops for good when this game is down this many dollars.', { unit: '$', step: 5, min: 1 }),
    ],
  },
  {
    title: 'Simulation and fees',
    knobs: [
      { key: 'shadowFillMode', label: 'fill model', hint: 'Which simulated-fill assumption drives a shadow runner\'s inventory. Queue = filled once the size ahead has traded.', kind: 'select', options: [
        { value: 'optimistic', label: 'optimistic' }, { value: 'queue', label: 'queue' }, { value: 'pessimistic', label: 'pessimistic' },
      ] },
      n('shadowLatencyMs', 'simulated latency', 'Order latency assumed by the simulator.', { unit: 'ms', step: 10, min: 0 }),
      n('makerFeeRate', 'maker fee rate', 'Kalshi maker fee coefficient for this series.', { step: 0.0005, min: 0 }),
      n('rebatePct', 'rebate', 'Share of the maker fee refunded (1 = 100%).', { step: 0.05, min: 0, max: 1 }),
    ],
  },
];

/** The basic tier: what every trader sees. Pinned by knobs.test.ts to hold no pull / pair / thin-book / depth knob. */
export const BASIC_KNOB_KEYS: ReadonlySet<string> = new Set(KNOB_GROUPS.flatMap((g) => g.knobs.filter((k) => k.tier === 'basic').map((k) => k.key)));

const knobByKey = (key: string): Knob => {
  const k = KNOB_GROUPS.flatMap((g) => g.knobs).find((x) => x.key === key);
  if (!k) throw new Error(`no knob ${key}`);
  return k;
};
/** The `pullPolicy` select's options, for callers that render it on their own. */
export const PULL_POLICIES: Array<{ value: 'cancelAll' | 'cancelAdding' | 'hold' | 'widen'; label: string }> = (() => {
  const k = knobByKey('pullPolicy');
  return k.kind === 'select' ? (k.options as Array<{ value: 'cancelAll' | 'cancelAdding' | 'hold' | 'widen'; label: string }>) : [];
})();

/** The shadow variants a trader can run beside their own settings, grouped by the question each answers. */
export const VARIANT_GROUPS: Array<{ label: string; names: string[] }> = [
  { label: 'resting orders', names: ['rest.stay', 'rest.chase', 'rest.edgeOff'] },
  { label: 'when Pinnacle pulls', names: ['pull.cancelAll', 'pull.cancelAdding', 'pull.hold2s', 'pull.widen2c'] },
  { label: 'pair lock', names: ['pair.lock1', 'pair.lock2', 'pair.lock3', 'pair.lock4'] },
  { label: 'edge demanded', names: ['value.edgeNegHalf', 'value.edge0', 'value.edge1'] },
  { label: 'thin book', names: ['liq.pause', 'liq.hold', 'liq.quote'] },
  { label: 'other', names: ['score.hold5s', 'legs.yesYes', 'legs.oneMarket'] },
];
